/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, TouchableOpacity, StyleSheet, Dimensions } from 'react-native';

import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';

import { isVn } from '@global/helpers';
import PropTypes from 'prop-types';

const propTypes = {
  data: PropTypes.array,
  addressType: PropTypes.string,
  onPressAddress: PropTypes.func,
  onAddAddress: PropTypes.func,
  onDelete: PropTypes.func,
  hideDelete: PropTypes.bool,
};

const Address = (props) => {
  const getResponsiveCardWidth = () => {
    if (!isWeb) {
      return 330;
    }

    const screenWidth = Dimensions.get('window').width;
    const maxContainerWidth = 1200;
    const effectiveWidth = Math.min(screenWidth, maxContainerWidth) - 90;
    const columns = 2;
    const spacing = 20;

    const totalGap = spacing * (columns - 1);
    const cardWidth = (effectiveWidth - totalGap) / columns;

    return Math.floor(cardWidth);
  };

  const getSmallerCardWidth = () => {
    if (!isWeb) {
      return 330;
    }

    const screenWidth = Dimensions.get('window').width;
    const maxContainerWidth = 1200;
    const effectiveWidth = Math.min(screenWidth, maxContainerWidth) - 160;
    const columns = 2;
    const spacing = 20;

    const totalGap = spacing * (columns - 1);
    const cardWidth = (effectiveWidth - totalGap) / columns;

    return Math.floor(cardWidth * 0.8);
  };

  const isCustomerDetailScreen = global.pageFrom === 'CustomerDetailScreen';
  const isHaveData = props.data?.length > 0;

  const formatNPWPValue = (value) => {
    if (isVn()) {
      return value;
    }

    value = value.replace(/\D/g, '');

    if (value.length > 16) {
      value = value.slice(0, 16);
    }

    return value;
  };

  const renderPremiumBadge = () => (
    <View style={styles.premiumBadge}>
      <SociollaText type="medium" style={styles.premiumBadgeText}>
        Premium
      </SociollaText>
    </View>
  );

  const renderNameSection = (name, isPremium, addressType) => (
    <View
      style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
      {isPremium &&
        (addressType === 'delivery' || addressType === 'store') &&
        renderPremiumBadge()}
      <SociollaText
        type="medium"
        numberOfLines={1}
        style={{
          fontSize: 14,
          color: Colors.black5,
          flex: 1,
          marginLeft:
            isPremium && (addressType === 'delivery' || addressType === 'store')
              ? 6
              : 0,
        }}>
        {name ? name : '-'}
      </SociollaText>
    </View>
  );

  const renderStreetSection = (street) => (
    <SociollaText
      style={{ fontSize: 14, color: Colors.black1, lineHeight: 20 }}
      numberOfLines={1}>
      {street ? street : '-'}
    </SociollaText>
  );

  const renderPhoneSection = (phone) => (
    <SociollaText
      style={{ fontSize: 14, color: Colors.black1, lineHeight: 20 }}>
      {phone && phone?.length > 0 ? phone.join(' - ') : '-'}
    </SociollaText>
  );

  const renderNumberIdentity = (numberIdentity) => (
    <SociollaText
      style={{ fontSize: 14, color: Colors.black1, lineHeight: 20 }}>
      {numberIdentity ? numberIdentity : '-'}
    </SociollaText>
  );

  const renderDeleteButton = (address, addressProps) => (
    <TouchableOpacity
      testID={`customer-address-delete-${addressProps.type}-${addressProps.index}`}
      style={styles.containerImage}
      onPress={() =>
        props.onDelete(address, addressProps.index, addressProps.type)
      }>
      <Image
        style={{ width: 20, height: 20 }}
        width={20}
        height={20}
        source={require('@assets/img/icons/delete.svg')}
        svg={true}
      />
    </TouchableOpacity>
  );

  const getBackgroundColor = (addressProps) => {
    let backgroundColor = 'rgba(29, 103, 252, 0.08)'; // Default background color for npwp card

    switch (addressProps.type) {
      case 'invoice':
        backgroundColor = 'rgba(252, 177, 29, 0.08)';
        break;
      case 'npwp':
        backgroundColor = '#E2F7E4';
        break;
      case 'ktp':
        backgroundColor = '#DEF1FF';
        break;
      case 'store':
        backgroundColor = '#FFE6E6';
        break;
      default:
        break;
    }

    return backgroundColor;
  };

  const getPhoneNumbers = (address) => {
    const phone = [];

    if (address.phone) {
      phone.push(address?.phone);
    }
    if (address.mobile) {
      phone.push(address?.mobile);
    }

    return phone;
  };

  const getTouchableProps = (backgroundColor, addressProps) => ({
    testID: `customer-address-card-${addressProps.type}-${addressProps.index}`,
    style: [
      styles.containerAddress,
      {
        backgroundColor,
        width: isCustomerDetailScreen
          ? getSmallerCardWidth()
          : getResponsiveCardWidth(),
      },
    ],
    onPress: () =>
      props.onPressAddress ? props.onPressAddress(addressProps) : false,
  });

  const AddressCard = (addressProps) => {
    const address = addressProps.data;
    const numberIdentity = address?.vat
      ? address?.vat
        ? formatNPWPValue(address?.vat)
        : ''
      : address?.ktp
      ? address?.ktp
      : '';
    const backgroundColor = getBackgroundColor(addressProps);
    const phones = getPhoneNumbers(address);

    return (
      <TouchableOpacity
        key={'address-' + addressProps.type + '-' + addressProps.index}
        {...getTouchableProps(backgroundColor, addressProps)}>
        {renderNameSection(address.name, address.is_premium, addressProps.type)}
        {renderStreetSection(address.street)}
        {phones.length > 0 && renderPhoneSection(phones)}
        {!!numberIdentity && renderNumberIdentity(numberIdentity)}
        {!props.hideDelete && renderDeleteButton(address, addressProps)}
      </TouchableOpacity>
    );
  };

  const renderAllCards = () =>
    props.data.map((del, index) => (
      <AddressCard
        data={del}
        index={index}
        type={props.addressType}
        key={`${props.addressType}` + index}
      />
    ));

  return (
    <View style={isWeb ? styles.oneFlex : {}}>
      {isHaveData && renderAllCards()}
    </View>
  );
};

Address.propTypes = propTypes;

const styles = StyleSheet.create({
  oneFlex: { flex: 1 },
  containerAddress: {
    borderRadius: 6,
    padding: 20,
    height: 126,
    marginBottom: isWeb ? 20 : 12,
  },
  containerImage: {
    position: 'absolute',
    right: 12,
    top: 12,
  },
  premiumBadge: {
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 10,
    backgroundColor: Colors.blue10,
    alignItems: 'center',
    flexShrink: 0,
  },
  premiumBadgeText: {
    color: Colors.white,
    fontSize: 10,
  },
});

export default Address;
